import { Helmet } from "react-helmet-async";

/**
 * Per-route SEO meta tags.
 *
 * Renders a full set of Google + Open Graph + Twitter Card tags for
 * the current route so each engine page has its own distinct
 * discoverability surface (rather than every page inheriting the
 * static landing-page tags from `/public/index.html`). Google rewards
 * per-route descriptions with better rank on engine-specific queries
 * like "spiritual warfare discernment tool" or "ladder of return
 * pastoral packet."
 *
 * Props are opinionated — pass the two required strings (title +
 * description) and everything else falls back to sensible brand
 * defaults so the caller stays terse. Optional `path` lets us set
 * an accurate canonical + og:url without importing useLocation
 * everywhere; if omitted we let the browser's current URL win.
 */
const SITE_ORIGIN = "https://restorationmatrix.org";
const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/icons/icon-512.png`;

export default function PageMeta({
  title,
  description,
  path,
  ogImage = DEFAULT_OG_IMAGE,
  noIndex = false,
}) {
  // A per-route <title> reads better in tab bars and search-result
  // listings when it carries the brand as a suffix. Landing intentionally
  // passes `title="The Restoration Matrix"` unsuffixed.
  const fullTitle = title.includes("Restoration Matrix")
    ? title
    : `${title} · The Restoration Matrix`;
  const canonicalHref = path ? `${SITE_ORIGIN}${path}` : SITE_ORIGIN + "/";

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalHref} />

      {/* Open Graph — controls the rendered card in iMessage,
       * Facebook, LinkedIn, WhatsApp, Discord, Slack, etc. */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="The Restoration Matrix" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalHref} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="512" />
      <meta property="og:image:height" content="512" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter / X card. summary_large_image renders the icon as
       * a prominent thumbnail rather than a small corner icon. */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Robots. Non-indexed pages (intake, packet detail, resonance
       * detail, progress detail, admin) opt into noIndex=true so
       * they stay out of search results without needing to touch
       * the static robots.txt. */}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
    </Helmet>
  );
}
